// src/components/UsageModal.jsx
// MyUsage modal (usage summary + limits)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

const mono = 'JetBrains Mono, monospace';
const display = 'Syne, sans-serif';

const MODULE_LABELS = {
  auto_eda: 'Auto EDA',
  doc_analysis: 'Document Analysis',
  image_insights: 'Image Insights',
  speech_studio: 'Speech Studio',
  ml_experiments: 'ML Experiments',
  dl_experiments: 'DL Experiments',
  video_intelligence: 'Video Intelligence',
};

function Spinner({ size = 16, color = 'var(--cyan)' }) {
  return (
    <span
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        border: `2px solid ${color}33`,
        borderTopColor: color,
        display: 'inline-block',
        animation: 'ddSpin .7s linear infinite',
        flexShrink: 0,
      }}
    />
  );
}

function pct(done, cap) {
  if (!cap || cap <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((done / cap) * 100)));
}

export default function UsageModal({ onClose }) {
  const overlayRef = useRef();
  const { isAuthenticated, tier, user } = useAuth();
  // Start in a "loading" posture when already authenticated so we don't flash empty/zero UI.
  const [loading, setLoading] = useState(() => !!isAuthenticated);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const isPremiumLocal = tier === 'annual' || tier === 'lifetime' || tier === 'researcher' || tier === 'pro';
  const isPremium = (data?.is_premium != null) ? !!data.is_premium : isPremiumLocal;

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    let timer = null;
    const startedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    setLoading(true);
    setErr('');
    // Force skeleton/spinner while fetching so we never display stale 0s.
    setData(null);
    api.usage.summary()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e?.data?.error || e?.message || 'Failed to load usage'); })
      .finally(() => {
        const endedAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
        const elapsed = Math.max(0, endedAt - startedAt);
        const minMs = 350;
        const wait = Math.max(0, minMs - elapsed);
        timer = setTimeout(() => { if (!cancelled) setLoading(false); }, wait);
      });
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [isAuthenticated, reloadKey]);

  const rows = useMemo(() => {
    if (!data?.modules) return [];
    const mods = data.modules || {};
    return Object.keys(MODULE_LABELS).map((id) => {
      const m = mods[id] || {};
      return {
        id,
        label: MODULE_LABELS[id] || id,
        done: Number(m.done || 0),
        cap: m.cap != null ? Number(m.cap) : null,
        left: m.left != null ? Number(m.left) : null,
      };
    });
  }, [data]);

  const openMembership = () => {
    window.dispatchEvent(new CustomEvent('open-membership', { detail: { focus: 'researcher' } }));
    onClose?.();
  };

  // Show skeleton immediately on open, even before the first effect flips `loading=true`.
  const isInitialLoading = isAuthenticated && !data && !err;
  const canShowData = isAuthenticated && !!data && !err && !isInitialLoading;

  return (
    <div
      ref={overlayRef}
      onClick={(e) => e.target === overlayRef.current && onClose?.()}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 230,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        background: 'rgba(2,5,8,.86)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 860,
          maxHeight: '84vh',
          overflow: 'hidden',
          borderRadius: 10,
          border: '1px solid var(--b1)',
          background: 'linear-gradient(180deg, rgba(34,211,238,.08), rgba(2,5,8,.92) 40%, rgba(2,5,8,.96) 100%)',
          boxShadow: '0 22px 70px rgba(0,0,0,.45)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--b0)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: display, fontWeight: 900, fontSize: 16, color: 'var(--t1)' }}>My Usage</div>
            <div style={{ marginTop: 4, fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>
              {isAuthenticated ? (user?.email || 'SIGNED IN') : 'GUEST'} / {isPremium ? 'PAID' : 'FREE'} TIER
              {data?.period ? ` / PERIOD ${data.period}` : ''}
            </div>
          </div>
          {isAuthenticated && (
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid var(--b1)',
                background: 'transparent',
                color: 'var(--t2)',
                fontFamily: mono,
                fontSize: 9,
                letterSpacing: '.12em',
                fontWeight: 900,
                cursor: 'pointer',
              }}
              title="Refresh usage"
            >
              {loading ? 'SYNC...' : 'REFRESH'}
            </button>
          )}
          {!isPremium && (
            <button
              onClick={openMembership}
              style={{
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid var(--cyan)55',
                background: 'var(--gcyan)',
                color: 'var(--cyan)',
                fontFamily: mono,
                fontSize: 9,
                letterSpacing: '.12em',
                fontWeight: 900,
                cursor: 'pointer',
              }}
            >
              UPGRADE
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: '1px solid var(--b1)',
              background: 'rgba(2,5,8,.35)',
              color: 'var(--t3)',
              cursor: 'pointer',
              fontSize: 14,
            }}
            aria-label="Close usage modal"
          >
            X
          </button>
        </div>

        <div style={{ padding: '14px', overflowY: 'auto', minHeight: 0, flex: 1 }}>
          {!isAuthenticated && (
            <div style={{ padding: 18, border: '1px solid var(--b0)', borderRadius: 8, background: 'rgba(2,5,8,.35)' }}>
              <div style={{ fontFamily: display, fontWeight: 900, fontSize: 16, color: 'var(--t1)', marginBottom: 6 }}>
                Sign in to track usage
              </div>
              <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.8, marginBottom: 12 }}>
                Usage is tied to your account. Create a free account to see done and remaining limits per module.
              </div>
              <button
                onClick={() => { window.dispatchEvent(new CustomEvent('open-auth', { detail: 'signup' })); onClose?.(); }}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--cyan)44',
                  background: 'var(--gcyan)',
                  color: 'var(--cyan)',
                  fontFamily: mono,
                  fontSize: 10,
                  letterSpacing: '.12em',
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                CREATE FREE ACCOUNT
              </button>
            </div>
          )}

          {isAuthenticated && (
            <>
              {isInitialLoading && (
                <div style={{ padding: '18px 14px', borderRadius: 10, border: '1px solid var(--b0)', background: 'rgba(2,5,8,.35)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                      <Spinner size={18} />
                      <div style={{ fontFamily: mono, fontSize: 10, color: 'var(--t3)', letterSpacing: '.08em' }}>
                      Syncing usage from server...
                      </div>
                    </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    {[0, 1, 2].map((i) => (
                      <div key={i} style={{ border: '1px solid var(--b0)', borderRadius: 8, background: 'rgba(2,5,8,.25)', padding: 12 }}>
                        <div className="dd-skel" style={{ height: 10, width: '60%', borderRadius: 6, marginBottom: 10 }} />
                        <div className="dd-skel" style={{ height: 18, width: '35%', borderRadius: 8 }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ marginTop: 12, border: '1px solid var(--b0)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ background: 'rgba(2,5,8,.25)', padding: '10px 12px', borderBottom: '1px solid var(--b0)' }}>
                      <div className="dd-skel" style={{ height: 10, width: '35%', borderRadius: 6 }} />
                    </div>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <div key={i} style={{ padding: '10px 12px', borderTop: '1px solid var(--b0)' }}>
                        <div className="dd-skel" style={{ height: 12, width: `${60 - i * 4}%`, borderRadius: 8, marginBottom: 6 }} />
                        <div className="dd-skel" style={{ height: 10, width: `${28 + (i % 3) * 8}%`, borderRadius: 8 }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {err && (
                <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--red)44', background: 'var(--red)12', color: 'var(--red)', fontFamily: mono, fontSize: 10 }}>
                  {err}
                  <button
                    onClick={() => setReloadKey((k) => k + 1)}
                    style={{ marginLeft: 10, padding: '6px 8px', borderRadius: 8, border: '1px solid var(--red)44', background: 'transparent', color: 'var(--red)', cursor: 'pointer', fontFamily: mono, fontSize: 9, letterSpacing: '.1em', fontWeight: 900 }}
                  >
                    RETRY
                  </button>
                </div>
              )}

              {canShowData && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
                  <div style={{ border: '1px solid var(--b0)', borderRadius: 8, background: 'rgba(2,5,8,.35)', padding: 12 }}>
                    <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>DONE (THIS MONTH)</div>
                    <div style={{ marginTop: 6, fontFamily: display, fontWeight: 900, fontSize: 22, color: 'var(--t1)' }}>{Number(data?.total_done || 0)}</div>
                  </div>
                  <div style={{ border: '1px solid var(--b0)', borderRadius: 8, background: 'rgba(2,5,8,.35)', padding: 12 }}>
                    <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>MODULES USED</div>
                    <div style={{ marginTop: 6, fontFamily: display, fontWeight: 900, fontSize: 22, color: 'var(--t1)' }}>
                      {rows.filter((r) => r.done > 0).length}
                    </div>
                  </div>
                  <div style={{ border: '1px solid var(--b0)', borderRadius: 8, background: 'rgba(2,5,8,.35)', padding: 12 }}>
                    <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>LAST DONE</div>
                    <div style={{ marginTop: 8, fontFamily: mono, fontSize: 10, color: 'var(--t2)', lineHeight: 1.6 }}>
                      {data?.last_done_at ? new Date(data.last_done_at).toLocaleString() : '-'}
                    </div>
                  </div>
                </div>
              )}

              {canShowData && (
                <>
                  {rows.every((r) => (r.done || 0) === 0) && (
                    <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--b0)', background: 'rgba(2,5,8,.35)', color: 'var(--t2)', fontSize: 11, lineHeight: 1.8 }}>
                      No completed runs yet. Upload a dataset/document and finish at least half the flow (or export a report) to record a DONE.
                    </div>
                  )}
                  <div style={{ border: '1px solid var(--b0)', borderRadius: 10, overflow: 'hidden' }}>
                    <div style={{ background: 'rgba(2,5,8,.35)', padding: '10px 12px', borderBottom: '1px solid var(--b0)', display: 'grid', gridTemplateColumns: isPremium ? '1fr 120px' : '1fr 90px 90px 1fr', gap: 10, alignItems: 'center' }}>
                      <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>MODULE</div>
                      {isPremium ? (
                        <div style={{ textAlign: 'right', fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>DONE</div>
                      ) : (
                        <>
                          <div style={{ textAlign: 'right', fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>DONE</div>
                          <div style={{ textAlign: 'right', fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>LEFT</div>
                          <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: '.14em', color: 'var(--t3)' }}>PROGRESS</div>
                        </>
                      )}
                    </div>
                    {rows.map((r) => (
                      <div key={r.id} style={{ padding: '10px 12px', borderTop: '1px solid var(--b0)', display: 'grid', gridTemplateColumns: isPremium ? '1fr 120px' : '1fr 90px 90px 1fr', gap: 10, alignItems: 'center' }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontFamily: display, fontWeight: 800, fontSize: 12, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.label}
                          </div>
                          {!isPremium && (
                            <div style={{ marginTop: 2, fontFamily: mono, fontSize: 9, color: 'var(--t3)', letterSpacing: '.08em' }}>
                              CAP {r.cap}
                            </div>
                          )}
                        </div>

                        <div style={{ textAlign: 'right', fontFamily: mono, fontSize: 11, color: 'var(--t1)' }}>{r.done}</div>

                        {isPremium ? null : (
                          <>
                            <div style={{ textAlign: 'right', fontFamily: mono, fontSize: 11, color: 'var(--t1)' }}>{r.left}</div>
                            <div style={{ height: 8, borderRadius: 999, background: 'rgba(255,255,255,.06)', border: '1px solid var(--b0)', overflow: 'hidden' }}>
                              <div style={{ width: `${pct(r.done, r.cap)}%`, height: '100%', background: pct(r.done, r.cap) >= 85 ? 'var(--red)' : 'var(--cyan)' }} />
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {!isPremium && (
                <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--gold)44', background: 'var(--gold)10', color: 'var(--t2)', fontSize: 11, lineHeight: 1.8 }}>
                  Free tier shows <span style={{ color: 'var(--t1)', fontWeight: 800 }}>DONE</span> and <span style={{ color: 'var(--t1)', fontWeight: 800 }}>LEFT</span>.
                  Paid tiers remove caps and show <span style={{ color: 'var(--t1)', fontWeight: 800 }}>DONE</span> plus monthly stats.
                </div>
              )}

              {loading && data && (
                <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Spinner size={14} />
                  <div style={{ fontFamily: mono, fontSize: 10, color: 'var(--t3)' }}>
                    Updating...
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes ddSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes ddShimmer { from { transform: translateX(-60%); } to { transform: translateX(180%); } }
        .dd-skel {
          position: relative;
          background: rgba(255,255,255,.06);
          border: 1px solid rgba(255,255,255,.08);
          overflow: hidden;
        }
        .dd-skel:after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(90deg, transparent, rgba(34,211,238,.16), transparent);
          transform: translateX(-60%);
          animation: ddShimmer 1.1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
